import React, { useMemo, useState } from 'react'

import { InfoIcon } from '@toasttab/buffet-pui-icons'
import { FieldType, useEditor } from '@toasttab/sites-components'
import classnames from 'classnames'
import Skeleton from 'react-loading-skeleton'
import isURL from 'validator/lib/isURL'

import { DiningOptionBehavior, ParentSlug } from 'src/apollo/onlineOrdering'
import { HeroContainer, HeroContentType, PageType } from 'src/apollo/sites'
import { t } from 'src/translations'

import Image, { ImageProps } from 'shared/components/common/Image'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { formatAddress } from 'shared/components/common/form_input/LocationInput'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { overrideConfigBasedOnTemplate } from 'public/components/default_template/menu_section/menuStylingOverrides'
import FulfillmentPanel from 'public/components/default_template/online_ordering/dining_behavior_toggle/FulfillmentPanel'
import ChangeLocationModal from 'public/components/default_template/online_ordering/location_selection/ChangeLocationModal'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import {
  useFulfillment,
  useOOLocations
} from 'public/components/online_ordering/FulfillmentContext'
import { Channel } from 'public/js/siteUtilities'

import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import ClosingTimeAlert from 'public/components/default_template/online_ordering/dining_behavior_toggle/ClosingTimeAlert'
import Breadcrumbs from '../product_display/ecommerce_product_display/navigation/Breadcrumbs'

type DisplayOptions = {
  showDiningOptionBehaviorMessage?: boolean
  showHeroImage?: boolean
}

type BreadcrumbOptions = {
  name: string
  parentSlugs?: ParentSlug[] | null
}

type MenuPageHeaderProps = {
  heading: string
  subHeading?: string
  heroConfig?: HeroContentType
  allowFulfillmentChange?: boolean
  displayOptions?: DisplayOptions
  refPath?: string
  additionalHeaders?: React.ReactNode
  breadcrumbs?: BreadcrumbOptions | null
  loading?: boolean
}

const MenuPageHeader = ({
  heading,
  subHeading,
  heroConfig,
  allowFulfillmentChange,
  displayOptions = {
    showDiningOptionBehaviorMessage: true,
    showHeroImage: true
  },
  refPath = 'config.ooConfig.heroImage',
  additionalHeaders,
  breadcrumbs,
  loading
}: MenuPageHeaderProps) => {
  const {
    restaurant: { config, additionalPages }
  } = useRestaurant()
  const { channel } = useCart()
  const { useEditableRef } = useEditor()
  const image = heroConfig?.heroImage
  const withoutImage = !image?.src
  const centerHero =
    !withoutImage && heroConfig?.heroContainer === HeroContainer.Center // undefined should also be treated as full width
  const heroBackground = heroConfig?.heroBackground
  const roundedCornerStyle = useMemo(
    () =>
      config.ooConfig && centerHero
        ? overrideConfigBasedOnTemplate(
            { format: config.ooConfig.format, colors: config.ooConfig.colors },
            config
          ).roundedCorner
        : undefined,
    [config, centerHero]
  )

  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'menu hero section',
    actions: ['move-up', 'move-down', 'duplicate', 'delete'],
    schema: {
      fields: [
        {
          path: refPath,
          displayName: 'Image',
          type: FieldType.Image,
          value: image,
          deletable: true
        }
      ]
    }
  })

  const imageProps: ImageProps = {
    alt: image?.altText || t('menu-page-header.banner-image'),
    className: roundedCornerStyle
  }

  const isMenuOnly = useMemo(() => {
    return (
      (additionalPages?.length === 1 &&
        additionalPages?.some((page) => page.pageType === PageType.Menu)) ||
      config.isMenuOnly
    )
  }, [additionalPages, config.isMenuOnly])

  return (
    <div
      ref={editableRef}
      className={classnames('menuHeader', { withoutImage })}
      data-testid='menu-header'
    >
      {channel !== Channel.ECOMM && !isMenuOnly && <FulfillmentWarningBanner />}
      {withoutImage || !displayOptions.showHeroImage ? (
        <div
          className='heroBanner withoutImage'
          data-testid='hero-without-image'
          style={{ background: heroBackground?.backgroundColor ?? undefined }}
        >
          <HeaderContent
            heading={heading}
            subHeading={subHeading}
            allowFulfillmentChange={allowFulfillmentChange}
            color={heroBackground?.textColor ?? undefined}
            additionalHeaders={additionalHeaders}
            showDiningOptionBehaviorMessage={
              displayOptions.showDiningOptionBehaviorMessage
            }
            breadcrumbs={breadcrumbs}
            loading={loading}
          />
        </div>
      ) : (
        <>
          <div
            className={classnames(
              'heroBanner',
              centerHero && 'paddedContentWrapper'
            )}
            data-testid='hero-with-image'
          >
            <div
              className={classnames(
                'heroImage',
                centerHero && 'paddedContent',
                { centerHero }
              )}
              data-testid='hero-image'
            >
              {/* Don't use the image object directly if the original source is a fully qualified path,
              since that means it was probably pulled from Toast's CDN and can't use the Imgproxy path */}
              {isURL(image.src, { require_host: true }) ? (
                <Image src={image.src} aria-hidden {...imageProps} />
              ) : (
                <Image imageObject={image} aria-hidden {...imageProps} />
              )}
            </div>
          </div>
          <HeaderContent
            heading={heading}
            subHeading={subHeading}
            allowFulfillmentChange={allowFulfillmentChange}
            additionalHeaders={additionalHeaders}
            showDiningOptionBehaviorMessage={
              displayOptions.showDiningOptionBehaviorMessage
            }
            breadcrumbs={breadcrumbs}
            loading={loading}
          />
        </>
      )}
    </div>
  )
}

const HeaderContent = ({
  heading,
  subHeading,
  allowFulfillmentChange,
  color,
  showDiningOptionBehaviorMessage,
  additionalHeaders,
  breadcrumbs,
  loading
}: {
  heading: string
  subHeading?: string
  allowFulfillmentChange?: boolean
  color?: string
  showDiningOptionBehaviorMessage?: boolean
  additionalHeaders?: React.ReactNode
  breadcrumbs?: BreadcrumbOptions | null
  loading?: boolean
}) => {
  return (
    <div className='contentWrapper paddedContentWrapper'>
      <div className='content paddedContent'>
        <div
          data-testid='headerContent'
          className={classnames('headerContent', { loading })}
          style={{ color }}
        >
          {loading ? (
            <>
              <div className='breadcrumb-container'>
                <Skeleton
                  width='60%'
                  containerClassName='loadingSkeleton'
                  containerTestId='breadcrumb-skeleton'
                />
              </div>
              <h2 className='heading'>
                <Skeleton
                  height='32px'
                  width='40%'
                  containerClassName='loadingSkeleton'
                  containerTestId='heading-skeleton'
                />
              </h2>
            </>
          ) : (
            <>
              {breadcrumbs && (
                <div className='breadcrumb-container'>
                  <Breadcrumbs
                    name={breadcrumbs.name}
                    parentSlugs={breadcrumbs.parentSlugs}
                  />
                </div>
              )}
              <h2
                className={classnames('heading', {
                  smaller: heading?.length > 30
                })}
              >
                {heading}
              </h2>
              {subHeading && subHeading}
            </>
          )}
          {showDiningOptionBehaviorMessage && <DiningOptionBehaviorMessage />}
          {allowFulfillmentChange && (
            // hide this version on desktop in favor of the one in the FulfillmentPanel
            <div className='hidden-sm-down'>
              <ClosingTimeAlert />
            </div>
          )}
        </div>
        {allowFulfillmentChange && <FulfillmentPanel />}
        {additionalHeaders && (
          <div className='additionalHeaders hidden-xs'>{additionalHeaders}</div>
        )}
      </div>
    </div>
  )
}

const DiningOptionBehaviorMessage = () => {
  const { cart, channel } = useCart()
  const { lastKnownValidDeliveryAddress } = useDelivery()
  const { fulfillmentData } = useFulfillment()
  const {
    selectedLocation,
    restaurant: {
      config: { ooConfig }
    }
  } = useRestaurant()
  const [locationModalOpen, setLocationModalOpen] = useState(false)
  const hasHeroImage = Boolean(ooConfig?.heroImage?.src)
  const ooLocations = useOOLocations()
  const { track } = useThrottledTracker()
  const { ooEnableMenuChangeLocation } = useFlags()

  const fulfillmentBehavior = useMemo(
    () =>
      fulfillmentData?.cartFulfillmentData.diningOptionBehavior ??
      DiningOptionBehavior.TakeOut,
    [fulfillmentData?.cartFulfillmentData.diningOptionBehavior]
  )
  const deliveryAddress = useMemo(
    () => (cart ? cart.order?.deliveryInfo : lastKnownValidDeliveryAddress),
    [cart, lastKnownValidDeliveryAddress]
  )
  const showChangeLocationButton = useMemo(
    // Disallow changing the location once a delivery address has been successfully entered
    // Also check if the feature is enabled via feature flag
    () =>
      channel !== Channel.ECOMM &&
      ooEnableMenuChangeLocation &&
      (ooLocations?.length ?? 0) > 1 &&
      !(
        fulfillmentBehavior === DiningOptionBehavior.Delivery && deliveryAddress
      ),
    [
      ooEnableMenuChangeLocation,
      ooLocations?.length,
      fulfillmentBehavior,
      deliveryAddress,
      channel
    ]
  )

  return (
    <div className='diningOptionBehaviorInfo'>
      <span>{formatAddress(selectedLocation)}</span>
      {showChangeLocationButton && (
        <>
          <button
            type='button'
            onClick={() => {
              track(TrackingEvents.CHANGE_LOCATION_BUTTON_CLICKED, {
                currentRestaurantGuid: selectedLocation?.externalId,
                page: 'order_page'
              })
              setLocationModalOpen(true)
            }}
            className={classnames(
              'changeLocationButton',
              hasHeroImage ? 'hasHeroImage' : undefined
            )}
          >
            {t('menu-page-header.change-location')}
          </button>
          <ChangeLocationModal
            isOpen={locationModalOpen}
            onClose={() => setLocationModalOpen(false)}
          />
        </>
      )}
    </div>
  )
}

const FulfillmentWarningBanner = () => {
  const { orderingDisabled } = useRestaurant()
  const {
    selectedDiningOptionBehavior,
    diningOptionBehaviorAvailability,
    setFulfillmentTimeModalOpen
  } = useFulfillment()

  const scheduledOrdersOnly = useMemo(() => {
    const behavior =
      selectedDiningOptionBehavior === DiningOptionBehavior.TakeOut
        ? diningOptionBehaviorAvailability.takeout
        : diningOptionBehaviorAvailability.delivery
    return Boolean(behavior?.scheduledAvailable && !behavior.asapAvailable)
  }, [selectedDiningOptionBehavior, diningOptionBehaviorAvailability])

  if (scheduledOrdersOnly) {
    return (
      <button
        className='fulfillmentWarningBanner'
        onClick={() => setFulfillmentTimeModalOpen(true)}
      >
        <InfoIcon size='xs' accessibility='decorative' />
        <span>{t('menu-page-header.only-scheduled-orders')}</span>
      </button>
    )
  }
  if (orderingDisabled) {
    return (
      <div className='fulfillmentWarningBanner'>
        <InfoIcon size='xs' accessibility='decorative' />
        <span>{t('menu-page-header.not-accepting-online-orders')}</span>
      </div>
    )
  }
  return null
}

export default MenuPageHeader
